"use client";
import dynamic from "next/dynamic";
import { Button } from "@/components/ui/button";
import { Moon, Sun, AlignRight, X } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { useTheme } from "next-themes";
import { useEffect, useRef, useState } from "react";

const CalCTAButton = dynamic(() => import("./cal-cta-button"), { ssr: false });

interface NavItem {
  label: string;
  linkType: "custom" | "page";
  url?: string;
  page?: {
    slug: {
      current: string;
    };
  };
  external?: boolean;
}

interface CTAButton {
  text?: string;
  linkType?: "custom" | "page" | "cal";
  url?: string;
  page?: {
    slug: {
      current: string;
    };
  };
  external?: boolean;
  variant?: "default" | "secondary" | "outline" | "ghost";
}

export default function NavbarClient({ 
  logoUrl, 
  navigationItems = [],
  ctaButton,
  enableCTA
}: { 
  logoUrl: string;
  navigationItems?: NavItem[];
  ctaButton?: CTAButton;
  enableCTA?: boolean;
}) {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuOpenedAtRef = useRef<number>(0);
  const MENU_OPEN_GRACE_MS = 400;

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    // Prevent body scroll when menu is open
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const shouldIgnoreMenuClose = () =>
    Date.now() - menuOpenedAtRef.current < MENU_OPEN_GRACE_MS;

  return (
    <>
      <header className="sticky top-0 z-40 pt-6 bg-bg/80 backdrop-blur-sm">
        <div className="container mx-auto px-6 h-20 flex items-center justify-between relative">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 sm:w-9 sm:h-9 rounded-full border-2 border-border overflow-hidden flex-shrink-0">
              <Image
                src={logoUrl}
                alt="Carla Heywood"
                width={36}
                height={36}
                priority
                className="object-cover w-full h-full"
              />
            </div>
            <Link href="/" className="uppercase font-bold text-text text-base">
              Carla Heywood
            </Link>
          </div>
          <nav className="hidden md:flex items-center space-x-7">
            {navigationItems.map((item) => {
              const href =
                item.linkType === "page"
                  ? `/${item.page?.slug.current}`
                  : item.url || "#";

              return (
                <Link
                  key={item.label}
                  href={href}
                  className="text-sm text-text hover:text-text-muted"
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noopener noreferrer" : undefined}
                >
                  {item.label}
                </Link>
              );
            })}
            <Button
              variant="ghost"
              size="icon"
              aria-label="Toggle dark mode"
              className="text-text hover:bg-btn-bg hover:text-btn-text rounded-full p-2"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            >
              {mounted &&
                (theme === "dark" ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                ))}
            </Button>
            {enableCTA && ctaButton && (
              <>
                {ctaButton.linkType === "cal" ? (
                  <CalCTAButton
                    text={ctaButton.text || "Book a Call"}
                    variant={ctaButton.variant || "secondary"}
                  />
                ) : (
                  <Button variant={ctaButton.variant || "secondary"} asChild>
                    <Link
                      href={
                        ctaButton.linkType === "page"
                          ? `/${ctaButton.page?.slug.current}`
                          : ctaButton.url || "#"
                      }
                      target={ctaButton.external ? "_blank" : undefined}
                      rel={
                        ctaButton.external ? "noopener noreferrer" : undefined
                      }
                    >
                      {ctaButton.text || "Book a Call"}
                    </Link>
                  </Button>
                )}
              </>
            )}
          </nav>
          {/* Mobile menu button - tap = click; backdrop ignores first click after open (tap-release) */}
          <div className="md:hidden relative z-[60]">
            <button
              type="button"
              className="rounded-full min-w-[44px] min-h-[44px] w-12 h-12 flex items-center justify-center text-text hover:bg-btn-bg hover:text-btn-text transition-colors p-0 touch-manipulation cursor-pointer relative z-[60] pointer-events-auto"
              onClick={() => {
                if (!mobileMenuOpen) menuOpenedAtRef.current = Date.now();
                toggleMobileMenu();
              }}
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? (
                <X className="w-9 h-9" />
              ) : (
                <AlignRight className="w-9 h-9" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu - outside header to avoid stacking context issues */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 z-[60] bg-bg/90 backdrop-blur-xl"
          onClick={(e) => {
            if (e.target !== e.currentTarget) return;
            if (shouldIgnoreMenuClose()) return;
            closeMobileMenu();
          }}
        >
          <div 
            className="container mx-auto px-6 py-6 h-full flex flex-col touch-manipulation"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center h-20 flex-shrink-0">
              <div className="flex justify-start items-center">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Toggle dark mode"
                  className="text-text hover:bg-btn-bg hover:text-btn-text rounded-full min-w-[44px] min-h-[44px] p-2"
                  onClick={() =>
                    setTheme(theme === "dark" ? "light" : "dark")
                  }
                >
                  {mounted &&
                    (theme === "dark" ? (
                      <Sun className="w-6 h-6" />
                    ) : (
                      <Moon className="w-6 h-6" />
                    ))}
                </Button>
              </div>
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full min-w-[44px] min-h-[44px] p-2"
                  onClick={closeMobileMenu}
                  aria-label="Close mobile menu"
                >
                  <X className="w-6 h-6" />
                </Button>
              </div>
            </div>
            <nav className="flex flex-col space-y-4 mt-4 flex-1 overflow-y-auto">
              {navigationItems.map((item) => {
                const href =
                  item.linkType === "page"
                    ? `/${item.page?.slug.current}`
                    : item.url || "#";

                return (
                  <Link
                    key={item.label}
                    href={href}
                    className="text-lg text-center text-text hover:text-text-muted font-medium min-h-[44px] flex items-center justify-center px-4 py-3 rounded-xl active:bg-btn-bg transition-colors touch-manipulation"
                    target={item.external ? "_blank" : undefined}
                    rel={item.external ? "noopener noreferrer" : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      closeMobileMenu();
                    }}
                  >
                    {item.label}
                  </Link>
                );
              })}
              {enableCTA && ctaButton && (
                <div className="pt-4">
                  {ctaButton.linkType === "cal" ? (
                    <CalCTAButton
                      className="w-full min-h-[44px]"
                      text={ctaButton.text || "Book a Call"}
                      variant={ctaButton.variant || "secondary"}
                      onClick={closeMobileMenu}
                    />
                  ) : (
                    <Button
                      className="w-full min-h-[44px]"
                      variant={ctaButton.variant || "secondary"}
                      asChild
                    >
                      <Link
                        href={
                          ctaButton.linkType === "page"
                            ? `/${ctaButton.page?.slug.current}`
                            : ctaButton.url || "#"
                        }
                        target={ctaButton.external ? "_blank" : undefined}
                        rel={
                          ctaButton.external
                            ? "noopener noreferrer"
                            : undefined
                        }
                        onClick={closeMobileMenu}
                      >
                        {ctaButton.text || "Book a Call"}
                      </Link>
                    </Button>
                  )}
                </div>
              )}
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
